/* eslint-disable no-unused-vars */
import { css, StyleSheet } from 'aphrodite'
import { Amplify, Auth } from 'aws-amplify'
import jwtDecode from 'jwt-decode'
import PropTypes from 'prop-types'
import React, { useContext, useEffect, useState } from 'react'
import ReactCodeInput from 'react-code-input'
import ReactGa from 'react-ga4'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import LiveContext from '../../components/Context/LiveContext'
import LayoutModal from '../../components/Layout/LayoutModal'
import UIButton from '../../components/UI/UIButton'
import UIInput from '../../components/UI/UIInput'
import UIPassword from '../../components/UI/UIPassword'
import config from '../../config/aws'
import INT from '../../config/index'
import { COLOR_GRAY__800 } from '../../lib/colors'
import ChangePasswordForm from './ChangePasswordForm'
import ForgotPassowrdForm from './ForgotPassowrdForm'

Amplify.configure({
  Auth: { ...config.aws, authenticationFlowType: 'USER_SRP_AUTH' },
})

const styles = StyleSheet.create({
  forgotAndManuelActiveButton: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  button: {
    marginTop: '10px',
  },
  subtitle: {
    fontFamily: 'Helvetica',
    fontSize: '18px',
    display: 'flex',
    fontWeight: '400',
    flexDirection: 'row',
    justifyContent: 'center',
    paddingBottom: '10px',
    marginTop: 0,
  },
  title: {
    fontWeight: 700,
    textAlign: 'center',
    fontSize: '1.2em',
    marginBottom: '30px',
    '@media (max-width: 480px)': {
      fontSize: '1.2em',
    },
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: 30,
    marginBottom: 30,
  },
  text: {
    fontWeight: 400,
    fontSize: '0.8em',
    color: COLOR_GRAY__800,
    textAlign: 'center',
  },
  btnSubmit: {
    marginTop: 30,
  },
  forgottenPasswordButton: {
    alignSelf: 'flex-start',
    paddingBottom: '20px',
  },
  labelCode: {
    color: 'rgb(113, 128, 150)',
    padding: '5px 0 15px 0',
    fontWeight: 400,
    fontSize: '14px',
    gap: '5px',
  },
})

const LoginPopup = ({ isOpened, onClose, onCreateAccount, onLoginSuccess }) => {
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({ mode: 'all' })

  const { t } = useTranslation('translation')
  const navigate = useNavigate()

  const {
    setUserLogged,
    onLoginSuccessContext,
    toggleLoginOpened,
    setIdWatcher,
    setUserRole,
  } = useContext(LiveContext)

  const [isLoading, setLoading] = useState(false)
  const [modalTitle, setModalTitle] = useState(t('pages.login.popInLoginTitle'))
  const [Mail, setMail] = useState('')

  const [showForgottenPasswordForm, setShowForgottenPasswordForm] =
    useState(false)
  const [passwordChangeRequest, setPasswordChangeRequest] = useState(false)
  const [forgotPasswordMail, setForgotPasswordMail] = useState('')
  const [accountVerification, setAccountVerification] = useState(false)
  const [isLoadingVerification, setLoadingVerification] = useState(false)
  const [manualActivation, setManualActivation] = useState(false)

  useEffect(() => {
    ReactGa.send(window.location.pathname + '/login')
  }, [])

  const onSubmit = async (data) => {
    try {
      setLoading(true)
      const user = await toast.promise(Auth.signIn(data.email, data.password), {
        pending: t('pages.login.pendingMessage'),
        error: t('pages.login.errorMessage'),
      })

      const userToken = user?.signInUserSession?.getIdToken().getJwtToken()
      const refreshToken = user?.signInUserSession?.getRefreshToken().getToken()

      if (userToken && refreshToken) {
        setLoading(false)
        if (jwtDecode(userToken).role === 'ubiqeyes_watcher') {
          localStorage.setItem('access_token', userToken)
          localStorage.setItem('refresh_token', refreshToken)
          setIdWatcher(jwtDecode(localStorage.getItem('access_token')).sub)
          setUserLogged(true)
          toggleLoginOpened(false)
          if (onClose) {
            onClose()
          }
          if (onLoginSuccess) {
            onLoginSuccess()
          }
          if (onLoginSuccessContext) {
            onLoginSuccessContext().subscribe()
          }
        } else {
          setUserLogged(true)
          toggleLoginOpened(false)
          localStorage.setItem('access_token', userToken)
          localStorage.setItem('refresh_token', refreshToken)
          navigate('/register-maker/documents')
        }
      }
      setUserRole(jwtDecode(userToken).role)
      toast.success(t('pages.login.successMessage'))
    } catch (error) {
      setLoading(false)
      if (error.message === 'User is not confirmed.') {
        setMail(data.email)
        setAccountVerification(true)
        toast.error(t('common.form.errorMessage.userNotConfirmed'))
      }
    }
  }

  const [valid, setValid] = useState(true)
  const [Digit, setCode] = useState('')
  const submitMailVerification = async (e) => {
    setCode(e)
    if (Digit.length == 6) {
      try {
        setLoadingVerification(true)
        await toast.promise(
          Auth.confirmSignUp(manualActivation ? e.email : Mail, Digit),
          {
            success: t('pages.confirm.success'),
            pending: t('pages.confirm.pending'),
            error: t('pages.confirm.error'),
          }
        )
        setValid(true)
        setAccountVerification(false)
        setModalTitle(t('pages.login.activationDuCompte'))
      } catch (error) {
        setValid(false)
        console.log(error)
      }
    }
  }

  const mailSet = (e) => {
    setMail(e)
    console.log(Mail)
  }

  return (
    <LayoutModal
      isOpened={isOpened}
      hasPadding
      headerLabel={modalTitle}
      hasReturn={
        showForgottenPasswordForm ||
        passwordChangeRequest ||
        accountVerification
      }
      onClose={() => {
        onClose()
        toggleLoginOpened(false)
        setPasswordChangeRequest(false)
        setShowForgottenPasswordForm(false)
        setAccountVerification(false)
        setManualActivation(false)
      }}
      onReturn={() => {
        if (showForgottenPasswordForm && !passwordChangeRequest) {
          setPasswordChangeRequest(false)
          setShowForgottenPasswordForm(false)
          setAccountVerification(false)
          setManualActivation(false)
          setModalTitle(t('pages.login.popInLoginTitle'))
        }
        if (passwordChangeRequest && showForgottenPasswordForm) {
          setPasswordChangeRequest(false)
          setShowForgottenPasswordForm(true)
          setAccountVerification(false)
          setManualActivation(false)
          setModalTitle(t('pages.login.forgottenPasswordTitle'))
        }
        if (accountVerification) {
          setPasswordChangeRequest(false)
          setShowForgottenPasswordForm(false)
          setAccountVerification(false)
          setManualActivation(false)
        }
      }}
    >
      {!showForgottenPasswordForm &&
      !passwordChangeRequest &&
      !accountVerification ? (
        <>
          <h2 className={css(styles.title)}>
            {t('pages.login.popInLoginSubTitle')}
          </h2>

          <UIButton
            id="creation-compte-popup"
            kind="button"
            design="tertiary"
            label={t('pages.login.createAccount')}
            fullWidth
            onClick={onCreateAccount}
          />
          <UIButton
            dynamicStyle={styles.button}
            kind="button"
            design="secondary"
            label={t('pages.login.becomeMaker')}
            fullWidth
            onClick={() => {
              if (INT.AUTH_PERM == true) {
                localStorage.removeItem('userMaker')
                localStorage.removeItem('registerStatus')
                navigate('/register-maker')
              }
            }}
          />
          <h2 className={css(styles.title)}>
            {t('pages.login.popInLoginSubTitle1')}
          </h2>
          <form onSubmit={handleSubmit(onSubmit)} className={css(styles.form)}>
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <UIInput
                  {...field}
                  label={t('pages.login.identifierOrEmail')}
                  placeholder={t('pages.login.enterYourEmail')}
                  errorMessage={errors.email && errors.email.message}
                />
              )}
              rules={{
                required: {
                  value: true,
                  message: t('common.form.errorMessage.emailEmptyError'),
                },
                pattern: {
                  value:
                    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                  message: t('common.form.errorMessage.invalidEmailEmptyError'),
                },
              }}
            />
            <Controller
              name="password"
              control={control}
              render={({ field }) => (
                <UIPassword
                  {...field}
                  label={t('common.form.password')}
                  placeholder={t('pages.login.enterYourPassword')}
                  errorMessage={errors.password && errors.password.message}
                />
              )}
              rules={{
                required: {
                  value: true,
                  message: t('common.form.errorMessage.passwordEmptyError'),
                },
              }}
            />
            <div className={css(styles.forgotAndManuelActiveButton)}>
              <UIButton
                dynamicStyle={styles.forgottenPasswordButton}
                kind="text"
                label={t('pages.login.manualActivation')}
                onClick={() => {
                  setAccountVerification(true)
                  setManualActivation(true)
                  setModalTitle(t('pages.login.activationDuCompte'))
                }}
              />
              <UIButton
                dynamicStyle={styles.forgottenPasswordButton}
                kind="text"
                label={t('pages.login.forgot')}
                onClick={() => {
                  setShowForgottenPasswordForm(true)
                  setModalTitle(t('pages.login.forgottenPasswordTitle'))
                  ReactGa.send(window.location.pathname + '/forgot-password')
                }}
              />
            </div>

            <UIButton
              dynamicStyle={styles.btnSubmit}
              type="submit"
              kind="button"
              design="tertiary"
              disabled={INT.AUTH_PERM == false ? true : isLoading}
              label={t('pages.login.logMeIn')}
              fullWidth
            />
          </form>
        </>
      ) : (
        !passwordChangeRequest &&
        !accountVerification && (
          <ForgotPassowrdForm
            onSendingMail={() => setPasswordChangeRequest(true)}
            getMailAddress={(mail) => setForgotPasswordMail(mail)}
          />
        )
      )}
      {passwordChangeRequest && !accountVerification && (
        <ChangePasswordForm
          email={forgotPasswordMail}
          onPasswordChanged={() => {
            setPasswordChangeRequest(false)
            setShowForgottenPasswordForm(false)
          }}
        />
      )}
      {accountVerification && (
        <>
          <h3 className={css(styles.title)}>{t('pages.login.waitingMail')}</h3>
          {manualActivation ? <></> : <h4>{Mail}</h4>}
          <form
            onSubmit={handleSubmit(submitMailVerification)}
            className={css(styles.form)}
          >
            {manualActivation ? (
              <Controller
                name="email"
                control={control}
                render={({ field }) =>
                  manualActivation ? (
                    <UIInput
                      {...field}
                      label={t('pages.login.mailToActivate')}
                      placeholder={t('pages.login.enterYourEmail')}
                      errorMessage={errors.email && errors.email.message}
                    />
                  ) : (
                    <UIInput
                      {...field}
                      label={t('pages.login.mailToActivate')}
                      placeholder={t('pages.login.enterYourEmail')}
                      errorMessage={errors.email && errors.email.message}
                      onChange={setMail}
                      value={Mail}
                    />
                  )
                }
                rules={{
                  required: {
                    value: true,
                    message: t('common.form.errorMessage.emailEmptyError'),
                  },
                  pattern: {
                    value:
                      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                    message: t(
                      'common.form.errorMessage.invalidEmailEmptyError'
                    ),
                  },
                }}
              />
            ) : (
              <></>
            )}
            <div className={css(styles.labelCode)}>
              {t('pages.login.codeToActivate')}
              <ReactCodeInput
                type="text"
                isValid={valid}
                onChange={(e) => {
                  manualActivation ? setCode(e) : submitMailVerification
                }}
                fields={6}
                disable={isLoadingVerification}
              />
            </div>
            <UIButton
              kind="button"
              type="submit"
              design="primary"
              label={t('pages.login.validateAccount')}
              fullWidth
              onClick={manualActivation ? () => {} : submitMailVerification}
            />
          </form>
        </>
      )}
    </LayoutModal>
  )
}

LoginPopup.propTypes = {
  isOpened: PropTypes.bool,
  onClose: PropTypes.func,
  onCreateAccount: PropTypes.func,
  onLoginSuccess: PropTypes.func,
}

export default LoginPopup
